import React, { useState } from 'react';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Table, Column } from '../../components/ui/Table';
import { Search, Eye, Download } from 'lucide-react';
import { Modal } from '../../components/ui/Modal';
interface BackofficeAudit {
  id: string;
  activityDateTime: string;
  moduleName: string;
  actionType: string;
  username: string;
  requestBody: string;
  responseBody: string;
}
export function BackofficeAuditLogs() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLog, setSelectedLog] = useState<BackofficeAudit | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Mock Data
  const auditLogs: BackofficeAudit[] = [{
    id: '1',
    activityDateTime: '2024-01-15 10:30:45',
    moduleName: 'Product',
    actionType: 'Create',
    username: 'admin@credsys.com',
    requestBody: '{"productName":"Personal Loan","productCode":"PL-001","loanAmount":{"min":10000,"max":500000}}',
    responseBody: '{"status":"success","productId":"P-001","message":"Product created successfully"}'
  }, {
    id: '2',
    activityDateTime: '2024-01-15 11:15:20',
    moduleName: 'Tax',
    actionType: 'Update',
    username: 'ops@credsys.com',
    requestBody: '{"taxId":"TAX-001","taxRate":18,"effectiveDate":"2024-02-01"}',
    responseBody: '{"status":"success","message":"Tax rate updated"}'
  }, {
    id: '3',
    activityDateTime: '2024-01-15 12:00:00',
    moduleName: 'Fee',
    actionType: 'Create',
    username: 'admin@credsys.com',
    requestBody: '{"feeCode":"FEE-PROC-001","feeName":"Processing Fee","feeType":"Percentage","value":2.5}',
    responseBody: '{"status":"success","feeId":"F001","message":"Fee profile created"}'
  }, {
    id: '4',
    activityDateTime: '2024-01-15 14:30:15',
    moduleName: 'Taxes on Fees',
    actionType: 'Update',
    username: 'finance@credsys.com',
    requestBody: '{"taxOnFeeId":"TOF-001","taxType":"VAT","taxValue":16}',
    responseBody: '{"status":"success","message":"Tax on fee updated"}'
  }, {
    id: '5',
    activityDateTime: '2024-01-15 15:45:30',
    moduleName: 'KYC Group',
    actionType: 'Create',
    username: 'compliance@credsys.com',
    requestBody: '{"groupName":"Enhanced KYC","requiredDocs":["CNIC","Utility Bill","Bank Statement"]}',
    responseBody: '{"status":"success","kycGroupId":"KYC-003","message":"KYC group created"}'
  }, {
    id: '6',
    activityDateTime: '2024-01-16 09:00:00',
    moduleName: 'Penalties',
    actionType: 'Update',
    username: 'admin@credsys.com',
    requestBody: '{"penaltyId":"PEN-001","penaltyAmount":500,"effectiveDate":"2024-02-01"}',
    responseBody: '{"status":"success","message":"Penalty updated"}'
  }, {
    id: '7',
    activityDateTime: '2024-01-16 10:20:00',
    moduleName: 'Product',
    actionType: 'Inactive',
    username: 'admin@credsys.com',
    requestBody: '{"productId":"P-005","status":"inactive","reason":"Product discontinued"}',
    responseBody: '{"status":"success","message":"Product marked as inactive"}'
  }, {
    id: '8',
    activityDateTime: '2024-01-16 11:30:00',
    moduleName: 'Fee',
    actionType: 'Delete',
    username: 'admin@credsys.com',
    requestBody: '{"feeId":"F010","reason":"Duplicate entry"}',
    responseBody: '{"status":"success","message":"Fee profile deleted"}'
  }];
  const filteredLogs = auditLogs.filter(log => log.moduleName.toLowerCase().includes(searchQuery.toLowerCase()) || log.actionType.toLowerCase().includes(searchQuery.toLowerCase()) || log.username.toLowerCase().includes(searchQuery.toLowerCase()));
  const handleViewJSON = (log: BackofficeAudit) => {
    setSelectedLog(log);
    setIsModalOpen(true);
  };
  const columns: Column<BackofficeAudit>[] = [{
    key: 'activityDateTime',
    label: 'Activity Date/Time',
    render: item => <span className="text-sm font-medium text-gray-900" data-id="element-1895">
          {item.activityDateTime}
        </span>
  }, {
    key: 'moduleName',
    label: 'Module Name',
    render: item => <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800" data-id="element-1896">
          {item.moduleName}
        </span>
  }, {
    key: 'actionType',
    label: 'Action Type',
    render: item => {
      const colors: Record<string, string> = {
        Create: 'bg-green-100 text-green-800',
        Update: 'bg-blue-100 text-blue-800',
        Delete: 'bg-red-100 text-red-800',
        Inactive: 'bg-gray-100 text-gray-800'
      };
      return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[item.actionType] || 'bg-gray-100 text-gray-800'}`} data-id="element-1897">
            {item.actionType}
          </span>;
    }
  }, {
    key: 'username',
    label: 'Username',
    render: item => <span className="text-sm text-gray-700" data-id="element-1898">{item.username}</span>
  }, {
    key: 'requestBody',
    label: 'Request Body',
    render: item => <span className="text-xs text-gray-500 font-mono truncate max-w-xs block" data-id="element-1899">
          {item.requestBody.substring(0, 50)}...
        </span>
  }, {
    key: 'responseBody',
    label: 'Response Body',
    render: item => <span className="text-xs text-gray-500 font-mono truncate max-w-xs block" data-id="element-1900">
          {item.responseBody.substring(0, 50)}...
        </span>
  }, {
    key: 'action',
    label: 'Action',
    render: item => <Button size="sm" variant="ghost" onClick={() => handleViewJSON(item)} data-id="element-1901">
          <Eye className="h-4 w-4 mr-1" data-id="element-1902" />
          View JSON
        </Button>
  }];
  return <div className="space-y-8 animate-in fade-in duration-500" data-id="element-1903">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-1904">
        <div data-id="element-1905">
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight" data-id="element-1906">
            Backoffice Audit Logs
          </h1>
          <p className="text-gray-500 mt-1" data-id="element-1907">
            System-level changes and configuration updates tracking
          </p>
        </div>
        <Button size="lg" className="shadow-sm" data-id="element-1908">
          <Download className="mr-2 h-5 w-5" data-id="element-1909" />
          Export Logs
        </Button>
      </div>

      {/* Search */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-1910">
        <div className="relative" data-id="element-1911">
          <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" data-id="element-1912" />
          <Input placeholder="Search by Module, Action Type, or Username..." className="pl-10 border-0 bg-gray-50 focus:bg-white transition-colors" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1913" />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-1914">
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-1915">
          <div className="text-sm text-gray-500" data-id="element-1916">Total Changes</div>
          <div className="text-2xl font-bold text-gray-900 mt-1" data-id="element-1917">2,847</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-1918">
          <div className="text-sm text-gray-500" data-id="element-1919">Active Users</div>
          <div className="text-2xl font-bold text-gray-900 mt-1" data-id="element-1920">12</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-1921">
          <div className="text-sm text-gray-500" data-id="element-1922">Modules Updated</div>
          <div className="text-2xl font-bold text-gray-900 mt-1" data-id="element-1923">8</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-1924">
          <div className="text-sm text-gray-500" data-id="element-1925">Today's Changes</div>
          <div className="text-2xl font-bold text-blue-600 mt-1" data-id="element-1926">23</div>
        </div>
      </div>

      {/* Table */}
      <Table columns={columns} data={filteredLogs} keyExtractor={item => item.id} data-id="element-1927" />

      {/* JSON Modal */}
      {selectedLog && <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Audit Log Details" data-id="element-1928">
          <div className="space-y-4" data-id="element-1929">
            <div data-id="element-1930">
              <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-1931">
                Activity Information
              </h4>
              <div className="bg-gray-50 p-3 rounded text-sm space-y-1" data-id="element-1932">
                <div data-id="element-1933">
                  <strong data-id="element-1934">Date/Time:</strong> {selectedLog.activityDateTime}
                </div>
                <div data-id="element-1935">
                  <strong data-id="element-1936">Module:</strong> {selectedLog.moduleName}
                </div>
                <div data-id="element-1937">
                  <strong data-id="element-1938">Action:</strong> {selectedLog.actionType}
                </div>
                <div data-id="element-1939">
                  <strong data-id="element-1940">User:</strong> {selectedLog.username}
                </div>
              </div>
            </div>

            <div data-id="element-1941">
              <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-1942">
                Request Body
              </h4>
              <pre className="bg-gray-900 text-green-400 p-4 rounded text-xs overflow-x-auto" data-id="element-1943">
                {JSON.stringify(JSON.parse(selectedLog.requestBody), null, 2)}
              </pre>
            </div>

            <div data-id="element-1944">
              <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-1945">
                Response Body
              </h4>
              <pre className="bg-gray-900 text-green-400 p-4 rounded text-xs overflow-x-auto" data-id="element-1946">
                {JSON.stringify(JSON.parse(selectedLog.responseBody), null, 2)}
              </pre>
            </div>
          </div>
        </Modal>}
    </div>;
}